import {
  SCHEDULED,
  COMPLETE,
  IN_PROGRESS,
  DELAYED,
  POSTPONED,
} from 'constants/eventStatus'
import { sportPages, bmrLeaguesPages } from 'constants/leagues'
import { gridModes } from 'constants/grid'
import { TENNIS, FIGHTING } from 'constants/sportIds'

import { values, groupBy, flatten, isEmpty, get } from 'lodash'

import { groupArrayOfObjectsByKey } from 'helpers/arrays'
import { getLinesWithBestOdds } from 'helpers/linesFormatter'

export const BMRODDS_SLUG = '/betting-odds/'
export const MAIN_DOMAIN = 'https://www.bookmakersreview.com'

export const isScheduledEvent = (es) => es === SCHEDULED || es === DELAYED

export const isCompletedEvent = (es) => es === COMPLETE

export const isLiveEvent = (es) => es === IN_PROGRESS

export const isPostponedEvent = (es) => es === POSTPONED

const getLineStatus = (oldLine, newLine, eventMtids) => {
  const isLineUpdate = oldLine && new Date(newLine.tim) > new Date(oldLine.tim)
  const sameLineid =
    oldLine && Number(oldLine.lineid) === Number(newLine.lineid)
  const greaterSequence =
    oldLine && Number(newLine.sequence) > Number(oldLine.sequence)
  const isOffLineUpdate = sameLineid && greaterSequence
  const isValidNewLine = !oldLine && eventMtids.includes(newLine.mtid)

  return { isLineUpdate, isValidNewLine, isOffLineUpdate }
}

export function linesUpdater(
  eventsState,
  { currentLines: lines },
  { forceUpdate, paids },
) {
  const newEventsState = { ...eventsState }

  const linesByEvent = groupBy(lines, 'eid')

  Object.keys(linesByEvent).forEach((eidKey) => {
    linesByEvent[eidKey].forEach((newLine) => {
      const { eid, paid, mtid, tim } = newLine
      const event = newEventsState[eid]

      if (!event || !event.currentLines) return

      let oldCurrentLines = event.currentLines

      if (!Array.isArray(oldCurrentLines)) {
        oldCurrentLines = [oldCurrentLines]
      }

      const eventMtids =
        (event.marketTypes &&
          event.marketTypes.map(({ mtid: value }) => value)) ||
        []
      const marketTypeLayout = gridModes.PARTICIPANTS
      const propertyToFind =
        gridModes.PARTICIPANTS === marketTypeLayout ? 'partid' : 'boid'
      const oldLine = oldCurrentLines.find(
        (line) =>
          line[propertyToFind] === newLine[propertyToFind] &&
          line.mtid === mtid,
      )
      const { isValidNewLine, isOffLineUpdate, isLineUpdate } = getLineStatus(
        oldLine,
        newLine,
        eventMtids,
      )

      if (isLineUpdate || isOffLineUpdate || isValidNewLine || forceUpdate) {
        const currentLines = oldCurrentLines.filter(
          (line) =>
            line[propertyToFind] !== newLine[propertyToFind] ||
            line.mtid !== newLine.mtid,
        )

        currentLines.push({
          ...newLine,
          pri: Number((newLine && newLine.pri) || 0),
          adj: Number((newLine && newLine.adj) || 0),
          lastUpdate: !forceUpdate && tim,
        })

        newEventsState[eid] = {
          ...event,
          currentLines: { ...event.currentLines, [paid]: currentLines },
        }
      }
    })

    if (newEventsState[eidKey]) {
      const linesArray = flatten(values(newEventsState[eidKey].currentLines))
      const filteredLines = isEmpty(paids)
        ? linesArray
        : linesArray.filter(({ paid: id }) => paids.includes(id))
      const newCurrentLines = filteredLines

      newEventsState[eidKey].currentLines = groupBy(newCurrentLines, 'paid')
    }
  })

  return newEventsState
}

export function currentLinesUpdater(eventsState, lines, options) {
  let newEventsState = { ...eventsState }
  const { paids, forceUpdate } = options || {}

  newEventsState = linesUpdater(
    newEventsState,
    { currentLines: lines },
    { paids, forceUpdate },
  )

  return newEventsState
}

export function eventsUpdater(eventsState, eventsUpdates = []) {
  const newEventsState = { ...eventsState }

  eventsUpdates.forEach((newEvent) => {
    const { eid, sequence } = newEvent
    const event = newEventsState[eid]

    if (!event || event.sequence > sequence) return

    Object.keys(newEvent).forEach((key) => {
      event[key] = newEvent[key]
    })

    newEventsState[eid] = { ...event }
  })

  return newEventsState
}

export function scoresUpdater(eventState, scores) {
  const newEventsState = { ...eventState }

  scores.forEach((score) => {
    const { eid, sequence } = score
    const event = newEventsState[eid]

    if (!event || !event.scores) return

    const oldsScores = [...event.scores]
    const oldScore = oldsScores.find(
      ({ pn, partid }) => pn === score.pn || partid === score.partid,
    )

    if (oldScore && oldScore.sequence > sequence) return

    const newScores = oldsScores.filter(
      ({ pn, partid }) => pn !== score.pn || partid !== score.partid,
    )

    newScores.push(score)
    newEventsState[eid] = {
      ...event,
      scores: newScores,
    }
  })

  return newEventsState
}

export const generateEventLink = (
  { spid, lid, eid, slg },
  leagueSlugFromConfig = [],
) => {
  let leagueSlug = leagueSlugFromConfig.find((slug) => get(slug, 'lid') === lid)
  const eventSlg = slg || 'event'

  if (leagueSlug) {
    leagueSlug = get(leagueSlug, 'slug', null)
  } else {
    leagueSlug = bmrLeaguesPages[lid] || sportPages[spid]
  }

  return leagueSlug
    ? `https://bookmakersreview.com/${leagueSlug}/matchups/${eventSlg}/odds/?eid=${eid}`
    : ''
}

export const parserEvents = (eventsData, paids, leagueSlugFromConfig) =>
  eventsData.reduce((eventsAcc, newEvent) => {
    const event = { ...newEvent }

    if (!event.currentLines) {
      event.currentLines = []
    }

    const filteredLines = paids
      ? event.currentLines.filter(({ paid: id }) => paids.includes(id))
      : event.currentLines
    const lines = filteredLines

    eventsAcc[event.eid] = {
      ...event,
      currentLines: groupArrayOfObjectsByKey(lines, 'paid'),
      bettingOptions: groupArrayOfObjectsByKey(event.bettingOptions, 'boid'),
      participants: groupArrayOfObjectsByKey(event.participants, 'partid'),
      eventLink: generateEventLink(event, leagueSlugFromConfig),
    }

    return eventsAcc
  }, {})

// eslint-disable-next-line max-params
const _filterLinesByMarket = (
  events,
  currentLinesGroupByEid,
  openingLinesGroupByEid,
  bestLinesGroupedByEid,
  consensusGroupByEid,
  consensusV2GroupByEid,
) => {
  const linesGroupFiltered = {}
  const consensusGroupFiltered = {}
  const consensusV2GroupFiltered = {}
  const openingLinesGroupFiltered = {}
  const bestLinesGroupFiltered = {}

  Object.keys(events).forEach((key) => {
    linesGroupFiltered[key] = get(currentLinesGroupByEid, key, [])
    consensusGroupFiltered[key] = get(consensusGroupByEid, key, [])
    consensusV2GroupFiltered[key] = get(consensusV2GroupByEid, key, [])
    openingLinesGroupFiltered[key] = get(openingLinesGroupByEid, key, [])
    bestLinesGroupFiltered[key] = get(bestLinesGroupedByEid, key, [])
  })

  return {
    linesGroupFiltered,
    consensusGroupFiltered,
    consensusV2GroupFiltered,
    openingLinesGroupFiltered,
    bestLinesGroupFiltered,
  }
}

const _setBestOddFlagWithBestLines = (bestLines) => {
  const bestPriceByPartid = bestLines.reduce(
    (prices, { pri, partid }) => ({ ...prices, [partid]: pri }),
    {},
  )

  return ({ pri, partid, ...currentLine }) => ({
    ...currentLine,
    partid,
    pri,
    isBestOdd: pri === bestPriceByPartid[partid],
  })
}

const _addBestOddFlagBasedOnBestLines = (lines, bestLines) =>
  lines.map(_setBestOddFlagWithBestLines(bestLines))

export function getMarketFormatFromEvent(event, mtid) {
  const market = get(event, 'marketTypes', []).find(
    ({ mtid: toFind }) => Number(mtid) === Number(toFind),
  )

  return get(market, 'settings.format', 'DEFAULT')
}

export const addBestOddFlagToLines = ({
  event,
  currentLines,
  markets,
  bestLines,
}) => {
  if (bestLines && bestLines.length)
    return _addBestOddFlagBasedOnBestLines(currentLines, bestLines)

  const linesByMtid = groupBy(currentLines, 'mtid')

  return Object.keys(linesByMtid).reduce((acc, mtid) => {
    const spreadType =
      (markets && markets[mtid]) || getMarketFormatFromEvent(event, mtid)
    const linesWithBestOdd = getLinesWithBestOdds(
      linesByMtid[mtid],
      spreadType,
      bestLines,
    )

    return acc.concat(linesWithBestOdd)
  }, [])
}

export const getLinesUpdater = ({
  events,
  currentLines,
  openingLines,
  bestLines,
  consensus,
  consensusV2,
}) => {
  const currentLinesGroupByPaid = {}
  const updatedEvents = { ...events }

  const {
    linesGroupFiltered,
    openingLinesGroupFiltered,
    consensusGroupFiltered,
    bestLinesGroupFiltered,
    consensusV2GroupFiltered,
  } = _filterLinesByMarket(
    events,
    groupBy(currentLines, 'eid'),
    groupBy(openingLines, 'eid'),
    groupBy(bestLines, 'eid'),
    groupBy(consensus, 'eid'),
    groupBy(consensusV2, 'eid'),
  )

  Object.keys(linesGroupFiltered).forEach((key) => {
    currentLinesGroupByPaid[key] = groupBy(
      addBestOddFlagToLines({
        event: updatedEvents[key],
        currentLines: linesGroupFiltered[key],
        bestLines,
      }),
      'paid',
    )
  })

  Object.keys(updatedEvents).forEach((key) => {
    if (consensusGroupFiltered[key]) {
      const currentLine = updatedEvents[key].consensus || []

      updatedEvents[key].consensus = isEmpty(consensusGroupFiltered[key])
        ? [...currentLine]
        : [...consensusGroupFiltered[key]]

      updatedEvents[key].lastUpdate = Date.now()
    }

    if (consensusV2GroupFiltered[key]) {
      const currentLine = updatedEvents[key].consensusV2 || []

      updatedEvents[key].consensusV2 = isEmpty(consensusV2GroupFiltered[key])
        ? [...currentLine]
        : [...consensusV2GroupFiltered[key]]

      updatedEvents[key].lastUpdate = Date.now()
    }

    if (currentLinesGroupByPaid[key]) {
      const currentLine = updatedEvents[key].currentLines || {}

      updatedEvents[key].currentLines = Object.keys(
        currentLinesGroupByPaid[key] || {},
      ).length
        ? { ...currentLinesGroupByPaid[key] }
        : { ...currentLine }

      updatedEvents[key].lastUpdate = Date.now()
    }

    if (openingLinesGroupFiltered[key]) {
      const currentLine = updatedEvents[key].openingLines || []

      updatedEvents[key].openingLines = isEmpty(openingLinesGroupFiltered[key])
        ? [...currentLine]
        : [...openingLinesGroupFiltered[key]]

      updatedEvents[key].lastUpdate = Date.now()
    }

    if (bestLinesGroupFiltered[key]) {
      const currentLine = updatedEvents[key].bestLines || []

      updatedEvents[key].bestLines = isEmpty(bestLinesGroupFiltered[key])
        ? [...currentLine]
        : [...bestLinesGroupFiltered[key]]

      updatedEvents[key].lastUpdate = Date.now()
    }
  })

  return updatedEvents
}

export const getHigherMaxSequence = (elements = []) =>
  elements.reduce((acc, element) => Math.max(acc, element.sequence), 0)

const buildShortName = (participant, useFullLastname = true) => {
  const firstName = get(participant, 'source.fn', '')
  const lastName = get(participant, 'source.lnam', false)

  if (!lastName) return firstName

  return useFullLastname
    ? `${firstName.charAt(0)}. ${lastName}`
    : `${firstName.charAt(0)}.${lastName.charAt(0)}`
}

const getTennisParticipants = (participant) =>
  get(participant, 'source.nam')
    ? get(participant, 'source.nam')
    : buildShortName(participant)

const getUfcParticipants = (participant) => {
  const ufcName = get(participant, 'source.nam')
  const ufcLastName = ufcName.split(' ')

  return ufcName ? ufcLastName[ufcLastName.length - 1] : ''
}

export const getParticipants = (participant, spid) => {
  const name =
    get(participant, 'source.abbr') ||
    get(participant, 'source.sn') ||
    get(participant, 'source.nam') ||
    get(participant, 'source.fn')

  if (spid === TENNIS) return getTennisParticipants(participant)
  if (spid === FIGHTING) return getUfcParticipants(participant)

  return name
}
